import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pause, Play, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEARTBEAT_MS = 15000;
const SEEK_TOLERANCE = 1.5; // seconds of drift allowed before we snap back

// Phones and tablets get the 720p rendition (smaller download, and the video is
// never displayed larger than that anyway); desktops get 1080p. Chosen once, on
// load — swapping src mid-playback would reload the video.
const pickSource = (data) => {
  if (!data) return "";
  const coarsePointer =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(pointer: coarse)").matches
      : false;
  const narrow = typeof window !== "undefined" && window.innerWidth < 1024;
  const saveData =
    typeof navigator !== "undefined" && navigator.connection
      ? navigator.connection.saveData
      : false;
  const preferSmall = coarsePointer || narrow || saveData;
  return (preferSmall && data.url_720) || data.url || data.url_720 || "";
};

const fmt = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * First-login welcome video. Customers must watch it before they can accept the
 * terms. The controls are removed and seeking is snapped back, but the real
 * enforcement is server-side: the backend credits only plausible forward
 * progress and requires the wall clock to match before marking it complete.
 */
const IntroVideoGate = ({ onDone }) => {
  const videoRef = useRef(null);
  const maxWatched = useRef(0);
  const started = useRef(false);

  const [info, setInfo] = useState(null);
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/customer/intro-video`)
      .then(({ data }) => {
        setInfo(data);
        setSrc(pickSource(data));
        maxWatched.current = data.last_position || 0;
        setPosition(data.last_position || 0);
      })
      .catch(() => setError("Could not load the welcome video. Please refresh the page."))
      .finally(() => setLoading(false));
  }, []);

  const beat = useCallback(() => {
    const el = videoRef.current;
    if (!el || el.paused || el.ended) return;
    axios
      .post(`${API}/customer/intro-video/heartbeat`, { position: el.currentTime })
      .catch(() => {/* transient; the next beat carries the position */});
  }, []);

  useEffect(() => {
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [beat]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  };

  const handlePlay = async () => {
    setHasStarted(true);
    setPlaying(true);
    if (started.current) return;
    started.current = true;
    try {
      await axios.post(`${API}/customer/intro-video/start`);
    } catch {
      /* the completion check re-reads state server-side */
    }
  };

  // Snap back if the position jumps ahead of what has actually been watched.
  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.currentTime > maxWatched.current + SEEK_TOLERANCE) {
      el.currentTime = maxWatched.current;
      return;
    }
    if (el.currentTime > maxWatched.current) maxWatched.current = el.currentTime;
    setPosition(el.currentTime);
  };

  const handleEnded = async () => {
    setFinishing(true);
    try {
      await axios.post(`${API}/customer/intro-video/heartbeat`, {
        position: videoRef.current ? videoRef.current.currentTime : 0,
      });
      await axios.post(`${API}/customer/intro-video/complete`);
      toast.success("Thanks for watching!");
      if (onDone) await onDone();
    } catch (e) {
      setFinishing(false);
      const detail = e.response?.data?.detail;
      setError(detail || "We couldn't confirm the video was watched. Please play it through again.");
      if (detail) toast.error(detail);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-white/70">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading your welcome video...
      </div>
    );
  }

  const duration = info?.duration || 0;
  const remaining = Math.max(0, duration - position);
  const pct = duration ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[#0d2a5a] border border-[#1a3a6a] rounded-lg p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <PlayCircle className="text-[#f4cc1e]" size={20} />
          <h1 className="text-xl font-bold text-white font-['Chivo']">Welcome to RoofNuts</h1>
        </div>
        <p className="text-sm text-white/70 mb-4">
          Please watch this short video all the way through. Once it finishes you'll be able to
          review and accept the terms. It can't be skipped or fast-forwarded.
        </p>

        {error && (
          <div className="mb-4 rounded-md border border-[#9a3406] bg-[#9a3406]/20 px-3 py-2 text-sm text-white">
            {error}
          </div>
        )}

        <div className="rounded-md overflow-hidden bg-black">
          <video
            ref={videoRef}
            src={src}
            className="w-full aspect-video"
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture
            controlsList="nodownload noplaybackrate noremoteplayback"
            onContextMenu={(e) => e.preventDefault()}
            onClick={togglePlay}
            onPlay={handlePlay}
            onPause={() => setPlaying(false)}
            onTimeUpdate={handleTimeUpdate}
            onRateChange={() => {
              const el = videoRef.current;
              if (el && el.playbackRate !== 1) el.playbackRate = 1;
            }}
            onLoadedMetadata={() => {
              const el = videoRef.current;
              if (el && info?.last_position) el.currentTime = info.last_position;
            }}
            onEnded={() => {
              setPlaying(false);
              handleEnded();
            }}
          />
        </div>

        <div className="mt-4">
          <div className="h-2 w-full rounded-full bg-[#091c3c] overflow-hidden">
            <div
              className="h-full bg-[#f4cc1e] transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-white/60">
            <span>{fmt(position)} / {fmt(duration)}</span>
            <span>
              {finishing ? "Confirming..." : remaining > 0 ? `${fmt(remaining)} remaining` : "Complete"}
            </span>
          </div>
        </div>

        {!finishing && (
          <button
            type="button"
            onClick={togglePlay}
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#f4cc1e] text-[#091c3c] font-semibold rounded-md px-5 py-2.5 hover:bg-[#f4cc1e]/90"
          >
            {playing ? <Pause size={16} /> : <Play size={16} />}
            {playing ? "Pause" : hasStarted ? "Resume" : "Play video"}
          </button>
        )}
      </div>
    </div>
  );
};

export default IntroVideoGate;
